import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Embed } from 'embed';
import { of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { PageFieldsCodes } from '@core/forms/page-fields-codes';

import { AdminService } from '../admin/admin.service';
import { PowerbiEmbedService } from '../../../powerbi/powerbi-embed.service';

export const DATA_IFRAME_CLICK_ATTR = 'data-iframe-click-attr';
export const DATA_IFRAME_ATTR = 'data-iframe-attr';

function getLinkReport(page) {
  const foundValue = page.pageValue.find((pageValue) => pageValue.attribute.code === PageFieldsCodes.ID_REPORT);
  if (!foundValue) return null;

  return foundValue.value;
}

@Component({
  selector: 'kt-export-report',
  templateUrl: './export-report.component.html',
  styleUrls: ['../max-width-page-view-components.scss'],
})
export class ExportReportComponent implements OnInit, OnDestroy {
  private reportId: string;
  private powerbiEmbed: Embed;
  private unsubscribe$ = new Subject();

  @ViewChild('reportContainer') private reportContainer;

  public error = false;
  constructor(
    private powerbiService: PowerbiEmbedService,
    private adminService: AdminService,
    private route: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const route$ = this.route.paramMap.pipe(takeUntil(this.unsubscribe$));

    route$.subscribe((paramMap) =>
      this.adminService
        .getPageBySlug(paramMap.get('page'))
        .pipe(map(getLinkReport), takeUntil(this.unsubscribe$))
        .subscribe((reportId) => {
          this.reportId = reportId;
          this.generateReport(this.reportId);
        }),
    );
  }

  async generateReport(reportId) {
    const observable$ = await this.powerbiService.generateReport(this.reportContainer.el.nativeElement, reportId);

    observable$.pipe(catchError(this.handleError), takeUntil(this.unsubscribe$)).subscribe((powerbiEmbed: Embed) => {
      if (!powerbiEmbed) {
        this.setError();
        return;
      }

      powerbiEmbed.iframe.style.border = 'none';
      const handleSetDataIframeAttr = this.setDataIframeAttr(powerbiEmbed.iframe);
      const handleSetDataIframeClickAttr = this.setDataIframeClickAttr(powerbiEmbed.iframe);

      this.powerbiEmbed = powerbiEmbed;
      this.powerbiEmbed.on('loaded', handleSetDataIframeAttr);

      this.powerbiEmbed.on('visualClicked', handleSetDataIframeClickAttr);
      this.powerbiEmbed.on('buttonClicked', handleSetDataIframeClickAttr);
      this.powerbiEmbed.on('pageChanged', handleSetDataIframeClickAttr);
      this.powerbiEmbed.on('dataHyperlinkClicked', handleSetDataIframeClickAttr);
      this.powerbiEmbed.on('selectionChanged', handleSetDataIframeClickAttr);
    });
  }

  private setDataIframeClickAttr = (iframe) => () => iframe.setAttribute(DATA_IFRAME_CLICK_ATTR, '');
  private setDataIframeAttr = (iframe) => () => iframe.setAttribute(DATA_IFRAME_ATTR, '');

  private setError() {
    this.error = true;
    this.changeDetector.detectChanges();
  }

  private handleError = () => {
    this.setError();
    return of(null);
  };

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
